import React, { useState, useEffect, useRef } from 'react'
import { Controller, FieldValues, Path } from 'react-hook-form'

import { ButtonTabs } from '@clientbase/clientbase-library'
import { Box, Stack, Typography } from '@mui/material'

import FrequencyControls from '../FrequencyControls'
import { FrequencyProps } from './Frequency'

const Frequency = <T extends FieldValues>({
  control,
  watch,
  setValue,
  storedCycleValue,
  setStoredCycleValue,
  isUnique,
  setIsUnique,
  setIsCharge,
  edit,
  editRecurrence,
  disableRecurrentTab,
  disableUniqueTab,
  onUserManualSelection,
  onUserManualFrequency,
  onUserEditingCustomCycles,
  itemsFields = [],
  setDisableRecurrentTab,
  setDisableUniqueTab,
  userManuallySelectedCycles = false,
  userEditingCustomCycles = false,
  isProductOrLink = false,
  hasPaidBillings = false
}: FrequencyProps<T>) => {
  const [, setFallbackValue] = useState<number>(0)
  const tabsValue = !isUnique ? 'recurrent' : 'unique'

  // Referência para armazenar o timeout de debounce
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Referência para armazenar os valores anteriores
  const prevValuesRef = useRef({
    totalCycles: watch('totalCycles' as Path<T>),
    totalCyclesCustom: watch('totalCyclesCustom' as Path<T>)
  })

  // Assinatura da composição: uuid + total_cycles de cada item, na ordem.
  //
  // O efeito abaixo lê `itemsFields` e os produtos do form para decidir se a cobrança
  // vira recorrente, mas dependia só de [edit, userManuallySelectedCycles,
  // userEditingCustomCycles] — nada o reexecutava quando a COMPOSIÇÃO mudava. Escolher
  // um plano de cobrança única e só depois acrescentar um plano mensal deixava o
  // formulário preso em "Única": o item de cobrança única ia para o payload com
  // total_cycles 0 ("todos os ciclos") e uma taxa de instalação virava mensalidade.
  const compositionSignature = itemsFields
    .map((_, index) => {
      const product = watch(`items.${index}.product` as Path<T>) as
        | { value?: string; total_cycles?: number }
        | undefined
      return `${product?.value ?? ''}:${product?.total_cycles ?? ''}`
    })
    .join('|')

  // Função para extrair informações do produto diretamente do formulário
  const getProductInfoFromForm = (productField: any) => {
    if (
      !productField ||
      !productField.value ||
      productField.value === 'custom'
    ) {
      return null
    }

    return {
      uuid: productField.value,
      totalCycles: productField.total_cycles,
      frequency: productField.frequency
    }
  }

  // useEffect para gerenciar lógica de produtos e ciclos
  useEffect(() => {
    // Se estiver no modo de edição, não alterar os valores automaticamente
    if (edit) {
      return
    }

    // Se o usuário selecionou manualmente os ciclos ou está editando o campo customizado, não fazer alterações automáticas
    if (userManuallySelectedCycles || userEditingCustomCycles) {
      return
    }

    // Verificar valores antes de qualquer alteração
    const currentTotalCycles = watch('totalCycles' as Path<T>)
    const currentTotalCyclesCustom = watch('totalCyclesCustom' as Path<T>)

    // Verificar se já está configurado corretamente para ciclos > 12
    const isAlreadyConfiguredForCustomCycles =
      currentTotalCycles === 13 &&
      currentTotalCyclesCustom &&
      Number(currentTotalCyclesCustom) > 12

    // Se já estiver configurado corretamente para ciclos > 12, não fazer nada
    if (isAlreadyConfiguredForCustomCycles) {
      // Atualizar valores de referência
      prevValuesRef.current = {
        totalCycles: currentTotalCycles,
        totalCyclesCustom: currentTotalCyclesCustom
      }

      return
    }

    // Limpa o timeout anterior se existir
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Cria um novo timeout com debounce de 300ms
    debounceRef.current = setTimeout(() => {
      // Verifica se há produtos selecionados antes de fazer qualquer coisa
      const hasSelectedProducts = itemsFields.some(
        (_, index) => watch(`items.${index}.product` as Path<T>)?.value
      )

      if (!hasSelectedProducts) {
        // Se não há produtos selecionados, ambas as abas devem estar habilitadas
        setDisableRecurrentTab?.(false)
        setDisableUniqueTab?.(false)
        return
      }

      // Verifica se há itens personalizados (não produtos)
      const hasCustomItems = itemsFields.some(
        (_, index) =>
          watch(`items.${index}.description` as Path<T>) &&
          !watch(`items.${index}.product` as Path<T>)?.value
      )

      // Obtém todos os produtos selecionados nos itens diretamente do formulário
      const selectedProducts = itemsFields
        .map((_, index) => {
          const product = watch(`items.${index}.product` as Path<T>)
          return getProductInfoFromForm(product)
        })
        .filter(Boolean)

      if (selectedProducts.length === 0) {
        // Se não há produtos selecionados após o filtro, ambas as abas devem estar habilitadas
        setDisableRecurrentTab?.(false)
        setDisableUniqueTab?.(false)
        return
      }

      // Verifica se há algum produto recorrente (totalCycles > 1 ou totalCycles = 0)
      const hasRecurrentProduct = selectedProducts.some(
        (product) =>
          product && (product.totalCycles > 1 || product.totalCycles === 0)
      )

      if (isUnique && hasRecurrentProduct && !hasCustomItems) {
        setIsUnique?.(false)
        setIsCharge?.(false)

        // Quando converter para recorrente, atualizar os ciclos baseado no produto recorrente
        const recurrentProducts = selectedProducts.filter(
          (product) =>
            product && (product.totalCycles > 1 || product.totalCycles === 0)
        )

        const prioritizedRecurrentProduct = recurrentProducts.sort((a, b) => {
          if (a && a.totalCycles === 0) return -1
          if (b && b.totalCycles === 0) return 1
          return (b?.totalCycles || 0) - (a?.totalCycles || 0)
        })[0]

        if (prioritizedRecurrentProduct) {
          if (prioritizedRecurrentProduct.totalCycles === 0) {
            // Produto com ciclos infinitos
            setValue?.('totalCycles' as Path<T>, 0 as any)
          } else if (prioritizedRecurrentProduct.totalCycles > 12) {
            // Produto com mais de 12 ciclos - usar "Outros"
            setValue?.('totalCycles' as Path<T>, 13 as any)
            setValue?.(
              'totalCyclesCustom' as Path<T>,
              String(prioritizedRecurrentProduct.totalCycles) as any
            )
            setStoredCycleValue?.(13)
          } else {
            // Produto com ciclos normais (2-12)
            setValue?.(
              'totalCycles' as Path<T>,
              prioritizedRecurrentProduct.totalCycles as any
            )
            setStoredCycleValue?.(prioritizedRecurrentProduct.totalCycles)
          }
        }
      }

      // Verifica se há apenas produtos e nenhum deles é recorrente
      const hasOnlyNonRecurrentProducts =
        selectedProducts.length > 0 && !hasCustomItems && !hasRecurrentProduct

      const hasMixedProducts =
        selectedProducts.length > 0 &&
        !hasCustomItems &&
        hasRecurrentProduct &&
        selectedProducts.some((product) => product && product.totalCycles === 1)

      // Verifica se há apenas produtos e pelo menos um deles é recorrente
      const hasOnlyProductsWithAtLeastOneRecurrent =
        selectedProducts.length > 0 && !hasCustomItems && hasRecurrentProduct

      if (selectedProducts.length === 0 || hasMixedProducts || hasCustomItems) {
        setDisableRecurrentTab?.(false)
        setDisableUniqueTab?.(false)
      } else {
        setDisableRecurrentTab?.(hasOnlyNonRecurrentProducts)
        setDisableUniqueTab?.(hasOnlyProductsWithAtLeastOneRecurrent)
      }

      const isFirstProductSelection =
        selectedProducts.length === 1 && !hasCustomItems
      if (
        !isUnique &&
        hasOnlyNonRecurrentProducts &&
        isFirstProductSelection &&
        !hasMixedProducts
      ) {
        setIsUnique?.(true)
        setIsCharge?.(true)
        setValue?.('totalCycles' as Path<T>, 1 as any)
        setStoredCycleValue?.(1)
      }

      // Obtém o total de ciclos atual do formulário
      const currentTotalCycles = watch('totalCycles' as Path<T>)

      let maxCycles = 0 // Inicializar com 0 para permitir cálculo limpo

      // Produto que determinará a frequência (prioridade para o que tem mais ciclos)
      let frequencyProduct: { totalCycles: number; frequency?: string } | null =
        null

      selectedProducts.forEach((product) => {
        if (!product) return

        const isRecurrentProduct =
          product.totalCycles > 1 || product.totalCycles === 0
        const currentIsRecurrent =
          frequencyProduct &&
          (frequencyProduct.totalCycles > 1 ||
            frequencyProduct.totalCycles === 0)
        // Se o produto tem ciclos infinitos (0), este tem prioridade máxima
        if (product.totalCycles === 0) {
          maxCycles = 0
          frequencyProduct = product
        } else if (isRecurrentProduct && !currentIsRecurrent) {
          maxCycles = product.totalCycles
          frequencyProduct = product
        } else if (isRecurrentProduct === currentIsRecurrent) {
          if (maxCycles === 0 && product.totalCycles > 0) {
            maxCycles = product.totalCycles
            frequencyProduct = product
          } else if (maxCycles !== 0 && product.totalCycles > maxCycles) {
            maxCycles = product.totalCycles
            frequencyProduct = product
          }
        }
      })

      // Atualiza o total de ciclos se for diferente do atual e o usuário não tiver selecionado manualmente
      // Importante: não atualiza se o usuário tiver selecionado manualmente (clicado na aba ou alterado o valor)
      const currentTotalCyclesCustom = watch('totalCyclesCustom' as Path<T>)

      // Verificar se já está configurado corretamente para ciclos > 12
      const isAlreadyConfiguredForCustomCycles =
        currentTotalCycles === 13 &&
        currentTotalCyclesCustom &&
        Number(currentTotalCyclesCustom) > 12

      // Se já estiver configurado corretamente para ciclos > 12, não fazer nada
      if (isAlreadyConfiguredForCustomCycles && maxCycles > 12) {
        // Não faz nada, mantém a configuração atual
      } else if (
        maxCycles !== currentTotalCycles &&
        !userManuallySelectedCycles &&
        !userEditingCustomCycles &&
        !hasCustomItems &&
        (selectedProducts.length === 1 || hasRecurrentProduct)
      ) {
        // Se maxCycles > 12, configurar para "Outros" (13) e definir totalCyclesCustom
        if (maxCycles > 12) {
          setValue?.('totalCycles' as Path<T>, 13 as any)
          setValue?.('totalCyclesCustom' as Path<T>, String(maxCycles) as any)
          setStoredCycleValue?.(13) // Atualizar storedCycleValue para manter consistência
        } else {
          setValue?.('totalCycles' as Path<T>, maxCycles as any)
          setStoredCycleValue?.(maxCycles) // Atualizar storedCycleValue para manter consistência
        }
      }

      // A FREQUÊNCIA não é mais definida aqui. Este bloco era uma segunda cópia do
      // "frequência = a do primeiro produto recorrente", e sobrescrevia a derivação feita
      // em SharedChargeFields — o merchant escolhia um plano semestral e o campo voltava
      // para Mensal, porque o mapa local nem tinha `custom` e caía no `|| 1`.
      //
      // Hoje a frequência é derivada da composição inteira (o plano de menor período dita),
      // em SharedChargeFields, via deriveRecurrenceFrequency. Aqui ficou só o total de
      // ciclos, que é o que este efeito realmente tem a ver.

      // Atualizar valores de referência após processamento
      setTimeout(() => {
        prevValuesRef.current = {
          totalCycles: watch('totalCycles' as Path<T>),
          totalCyclesCustom: watch('totalCyclesCustom' as Path<T>)
        }
      }, 200)
    }, 300) // Debounce de 300ms

    // Limpa o timeout quando o componente for desmontado
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    edit,
    userManuallySelectedCycles,
    userEditingCustomCycles,
    compositionSignature
  ])

  return (
    <Stack gap={2} my={2}>
      {/* Para produtos/links sempre mostrar, para cobranças/recorrências só quando não está editando */}
      {(!edit || isProductOrLink) && (
        <>
          <Typography variant="mdBold">Tipo da cobrança</Typography>

          <Box
            sx={{
              borderRadius: '6px'
            }}
          >
            <Box mb={isUnique ? 0 : 0}>
              <Controller
                name={'totalCycles' as Path<T>}
                control={control}
                render={({ field }) => (
                  <>
                    <ButtonTabs
                      id="periodicity-tabs"
                      value={tabsValue}
                      onChange={(_, newValue) => {
                        const newNewValue = newValue as string

                        // Notify parent that user has manually changed the tab
                        // This is important to prevent automatic switching back
                        onUserManualSelection?.()

                        if (newNewValue === 'unique') {
                          field.onChange(1)

                          setIsUnique?.(true)
                          // Só alterar isCharge se não estiver no modo de edição
                          if (!edit) {
                            setIsCharge?.(true)
                          }
                          setFallbackValue(field.value)
                        } else {
                          // Quando mudar para recorrente, usar o storedCycleValue se disponível
                          // ou definir como 0 (Tempo Indeterminado) por padrão
                          const newCycleValue =
                            storedCycleValue && storedCycleValue > 1
                              ? storedCycleValue
                              : 0
                          field.onChange(newCycleValue)
                          setIsUnique?.(false)
                          // Só alterar isCharge se não estiver no modo de edição
                          if (!edit) {
                            setIsCharge?.(false)
                          }
                          setStoredCycleValue?.(newCycleValue)
                        }
                      }}
                      tabs={[
                        {
                          label: 'Única',
                          value: 'unique',
                          disabled: disableUniqueTab
                        },
                        {
                          label: 'Recorrente',
                          value: 'recurrent',
                          disabled: editRecurrence || disableRecurrentTab
                        }
                      ]}
                    />
                  </>
                )}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Show frequency controls when not in edit mode OR when editing a recurrence */}
      {(!edit || (edit && !isUnique)) && (
        <FrequencyControls
          watch={watch}
          control={control}
          setValue={setValue}
          setStoredCycleValue={setStoredCycleValue}
          storedCycleValue={storedCycleValue}
          isUnique={isUnique}
          setIsUnique={setIsUnique}
          isChargeOrRecurrenceForm={true}
          isEditingRecurrence={edit && editRecurrence}
          hasPaidBillings={hasPaidBillings}
          onUserManualSelection={onUserManualSelection}
          onUserManualFrequency={onUserManualFrequency}
          onUserEditingCustomCycles={onUserEditingCustomCycles}
        />
      )}
    </Stack>
  )
}

export default Frequency
